import { useQuery } from "@tanstack/react-query";
import {
  Button,
  DatePicker,
  Form,
  Input,
  InputNumber,
  message,
  Radio,
  Select,
  Space,
} from "antd";
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import {
  createCampaign,
  getCampaign,
  getRewards,
  getSchemas,
  updateCampaign,
} from "../actions/acc_actions";
import ContentLayout from "../components/layout/ContentLayout";
import Loading from "../utils/Loading";

const { Option } = Select;

export default function CampaignsForm() {
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const { id } = useParams();
  const [loading, setLoading] = useState(false);

  const [targetValue, setTargetValue] = useState("all");
  const [notificationValue, setNotificationValue] = useState("standard");
  const accUrn = useSelector((state) => state.account.urn);
  const businessUrn = useSelector((state) => state.account.businessUrn);

  console.log("accUrn", accUrn);
  console.log("businessUrn", businessUrn);

  const { isLoading: campaignLoading, data: campaignData } = useQuery({
    queryKey: ["campaign"],
    queryFn: () => getCampaign(id),
    enabled: id ? true : false,
  });

  const { isLoading: schemasLoading, data: schemasData } = useQuery({
    queryKey: ["schemas", businessUrn],
    queryFn: () => getSchemas(businessUrn, "VOUCHER"),
    enabled: businessUrn ? true : false,
  });

  console.log("schemasData", schemasData);

  const { isLoading: rewardsLoading, data: rewardsData } = useQuery({
    queryKey: ["rewards", businessUrn],
    queryFn: () => getRewards(businessUrn),
    enabled: businessUrn ? true : false,
  });

  const timeZones = Intl.supportedValuesOf("timeZone", { region: "all" });

  // Set initial form values when campaignData is loaded
  useEffect(() => {
    if (campaignData?.body && id) {
      const campaign = campaignData.body;
      // Set initial values for the form fields
      form.setFieldsValue({
        voucher_loyalty_scheme_urn: campaign.voucher_loyalty_scheme_urn,
        reward_type_urn: campaign.reward_type_urn,
        target_timezone: campaign.target_timezone,
        deliver_at: dayjs(campaign.deliver_at),
        expire_at: dayjs(campaign.expire_at),
        campaign_name: campaign.campaign_name,
        promotion_urn: campaign.promotion_urn,
        description: campaign.description,
        note: campaign.note,
        min_rewards_earned: campaign.min_rewards_earned,
        lapsed_exclude_from: campaign.lapsed_exclude_from
          ? dayjs(campaign.lapsed_exclude_from)
          : null,
      });

      setTargetValue(campaign.target_segment);
      setNotificationValue(campaign.notification_delivery_type);
    }
  }, [campaignData, id, form]);

  if (schemasLoading || rewardsLoading || campaignLoading) {
    return <Loading />;
  }

  const onChange = (e) => {
    setTargetValue(e.target.value);
  };

  const onNotificationChange = (e) => {
    setNotificationValue(e.target.value);
  };

  const onFinish = (values) => {
    // Manually add the file to the form data
    const formData = {
      ...values,
      account_urn: accUrn,
      business_urn: businessUrn,
      target_segment: targetValue,
      notification_delivery_type: notificationValue,
      deliver_at: values.deliver_at.toISOString(),
      expire_at: values.expire_at.toISOString(),
      lapsed_exclude_from: values?.lapsed_exclude_from?.toISOString(),
    };
    setLoading(true);

    if (campaignData) {
      updateCampaign(id, formData)
        .then(() => {
          navigate("/campaigns");
          message.success("campaign updated successfully.");
        })
        .catch(() => {
          message.error("Something went wrong !!");
        })
        .finally(() => {
          setLoading(false);
        });
    } else {
      createCampaign(formData)
        .then(() => {
          navigate(-1);
          message.success("campaign created successfully.");
        })
        .catch((err) => {
          message.error("Something went wrong !!");
        })
        .finally(() => {
          setLoading(false);
        });
    }
  };

  const disabledDate = (current) => {
    // Can not select days before today and today
    return current && current < dayjs().endOf("day");
  };
  return (
    <ContentLayout
      header={
        <>
          <h1>
            Setup New <span> Campaign Outlets</span>
          </h1>
          <p className="content_layout-subtitle">Outlets Setup</p>
        </>
      }
    >
      <Form
        form={form}
        className="rewards-form"
        onFinish={onFinish}
        layout="vertical"
      >
        <div className="rewards-form-section">
          <h4>Basic</h4>
          <div className="rewards-inputs">
            <Form.Item
              name="voucher_loyalty_scheme_urn"
              label="Which business is this campaign for?"
              rules={[{ required: true }]}
            >
              <Select
                options={schemasData?.data?.body.map((item) => ({
                  label: item.name,
                  value: item.urn,
                }))}
              />
            </Form.Item>
            <Form.Item
              name="reward_type_urn"
              label="Select Reward"
              rules={[{ required: true }]}
            >
              <Select
                options={rewardsData?.data.map((item) => ({
                  label: item.name,
                  value: item.urn,
                }))}
              />
            </Form.Item>
            <Form.Item
              rules={[{ required: true }]}
              label="Time Zone"
              name="target_timezone"
            >
              <Select>
                {timeZones.map((zone) => (
                  <Option key={zone} value={zone}>
                    {zone}
                  </Option>
                ))}
              </Select>
            </Form.Item>
            <Form.Item
              name="deliver_at"
              label="Deliver At"
              rules={[{ required: true }]}
            >
              <DatePicker
                format="YYYY-MM-DD HH:mm:ss"
                disabledDate={disabledDate}
                showTime={{ defaultValue: dayjs("00:00:00", "HH:mm:ss") }}
              />
            </Form.Item>{" "}
            <Form.Item
              name="expire_at"
              label="Expire At"
              dependencies={["deliver_at"]}
              rules={[
                { required: true },
                ({ getFieldValue }) => ({
                  validator: (_, value) =>
                    !value || !getFieldValue("deliver_at") || value.isAfter(getFieldValue("deliver_at"))
                      ? Promise.resolve()
                      : Promise.reject(new Error("Expire At must be after Deliver At")),
                }),
              ]}
            >
              <DatePicker
                format="YYYY-MM-DD HH:mm:ss"
                disabledDate={disabledDate}
                showTime={{ defaultValue: dayjs("00:00:00", "HH:mm:ss") }}
              />
            </Form.Item>
          </div>
        </div>

        <div className="rewards-form-section">
          <h4>Campaign</h4>
          <div className="rewards-inputs">
            <Form.Item rules={[{ required: true }]} label="Target Segment">
              <Radio.Group onChange={onChange} value={targetValue}>
                <Space direction="vertical">
                  <Radio value="all">All</Radio>
                  <Radio value="has-stamps">Has Stamps</Radio>
                  <Radio value="lapsed">Lapsed</Radio>
                  <Radio value="rewards-earned">Rewards Earned</Radio>
                </Space>
              </Radio.Group>
            </Form.Item>

            {targetValue === "lapsed" && (
              <Form.Item
                name="lapsed_exclude_from"
                label="Lapsed Exclude From"
                rules={[{ required: true, message: "Expire At is required" }]}
              >
                <DatePicker
                  format="YYYY-MM-DD HH:mm:ss"
                  showTime={{ defaultValue: dayjs("00:00:00", "HH:mm:ss") }}
                />
              </Form.Item>
            )}

            {targetValue === "rewards-earned" && (
              <Form.Item
                rules={[{ required: true }]}
                name="min_rewards_earned"
                label="Min Rewards Earned"
              >
                <InputNumber min={1} />
              </Form.Item>
            )}
          </div>
        </div>

        <div className="rewards-form-section">
          <h4>Notification</h4>
          <div className="rewards-inputs">
            <Form.Item
              name="alert"
              label="We'll alert customers that have received a reward in the app."
              rules={[{ required: true }]}
            >
              <Radio.Group
                onChange={onNotificationChange}
                value={notificationValue}
                defaultValue={notificationValue}
              >
                <Space direction="vertical">
                  <Radio value="standard">
                    Standard delivery - Send the notification at the time
                    specified above.
                  </Radio>
                  <Radio value="delayed-last-active">
                    Intelligent delivery - Send the notification at the time
                    each user is most likely to open it.
                  </Radio>
                </Space>
              </Radio.Group>
            </Form.Item>
            <Form.Item required name="note" label="Anything to add?">
              <Input.TextArea style={{ resize: "none" }} rows={6} />
            </Form.Item>
            {/* The message is saved into promotion.description, which holds at most 512 characters. */}
            <Form.Item
              name="description"
              label="Voucher Message"
              rules={[
                { required: true, whitespace: true, message: "Please enter the voucher message" },
                { max: 512, message: "Voucher Message must be at most 512 characters" },
              ]}
            >
              <Input.TextArea style={{ resize: "none" }} rows={6} count={{ show: true, max: 512 }} />
            </Form.Item>
          </div>
        </div>

        <Form.Item className="rewards-form-btn">
          <Button htmlType="submit" loading={loading} type="primary">
            {id ? "Update" : "Submit"}
          </Button>
        </Form.Item>
      </Form>
    </ContentLayout>
  );
}
